/* eslint-disable */
import { DateRange, Filter, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import numeral from 'numeral';
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import NoPermissions from '../../../components/NoPermissions';
import { DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS, DOCUMENT_SEARCH_ALL_ROUTE, DOCUMENT_SEARCH_REPORTS_ROUTE, DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE } from '../../../Constants';
import useFetchDocumentSearchData from '../../../hooks/document-search/useFetchDocumentSearchData';
import useFetchDocumentSearchFilters from '../../../hooks/document-search/useFetchDocumentSearchFilters';
import useFetchProfile from '../../../hooks/user/useFetchProfile';

import './DocumentSearch.scss';
import DocumentSearchHighlights from './DocumentSearchHighlights';
import DocumentSearchResultsTable from './DocumentSearchResultsTable';

function DocumentSearch({ docType, reportType }) {
  const PAGE_SIZE = 25;

  const INITIAL_DATE_RANGE = { startDate: null, endDate: null, relativeRange: null };
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [filterConfig, setFilterConfig] = useState<any>({});
  const [issueDateFilter, setIssueDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const [maturityDateFilter, setMaturityDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const { data: filters, error: filterError, isLoading: filtersLoading } = useFetchDocumentSearchFilters(docType);
  const [text, setText] = useState('');
  const [searchPhrase, setSearchPhrase] = useState('');
  const [sort, setSort] = useState<any>({ field: null, order: 'desc' });
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchDocumentSearchData(docType, sort, searchPhrase, reportType, filterConfig, issueDateFilter, maturityDateFilter, PAGE_SIZE);
  const { data: profile, isLoading: isProfileLoading, isFetched: isProfileFetched } = useFetchProfile();

  useEffect(() => {
    if (search) {
      try {
        const config = JSON.parse(decodeURI(search.substring(1)));
        setSort(config.sort);
        setFilterConfig(config.filterConfig);
        setIssueDateFilter(config.issueDateFilter);
        setMaturityDateFilter(config.maturityDateFilter);
        setSearchPhrase(config.searchPhrase);
        setText(config.searchPhrase);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    navigate(`${pathname}?${fetchUrlState()}`, {
      replace: true,
    });
  }, [sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter]);

  const fetchUrlState = () => {
    return `${JSON.stringify({ sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter })}`;
  };

  const updateUrl = () => {
    navigate(`${pathname}?${JSON.stringify({ sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter })}`, {
      replace: true,
    });
  };

  const updateSort = (value) => {
    setSort((config) => {
      if (config && config.field && config.field === value) {
        return {
          field: value,
          order: config.order === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return {
          field: value,
          order: 'asc',
        };
      }
    });
  };

  const resetFilters = () => {
    setFilterConfig({});
    setSearchPhrase('');
    setText('');
    setIssueDateFilter({ ...INITIAL_DATE_RANGE });
    setMaturityDateFilter({ ...INITIAL_DATE_RANGE });
  };

  const handleIssueDateChange = (value) => {
    setIssueDateFilter(value);
  };

  const handleMaturityDateChange = (value) => {
    setMaturityDateFilter(value);
  };

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config: any) => {
      config[key] = selected;

      if (selected.length === 0) {
        delete config[key];
      }

      return { ...config };
    });
  };

  const updateSearchPhrase = () => {
    setSearchPhrase(text);
  };

  const renderFilters = () => {
    return (
      <div className="filters-ctx">
        {filters.map((item) => (
          <Filter key={item.id} data={item.data} placeholder={item.name} checkedKeys={filterConfig[item.id] || []} onChange={(checked) => updateFilterConfig(item.id, checked)} />
        ))}
        <DateRange label="Issue Date" startDate={issueDateFilter.startDate} endDate={issueDateFilter.endDate} relativeRange={issueDateFilter.relativeRange} resetInput={0} error={false} setError={() => false} onChange={handleIssueDateChange} />
        <DateRange
          label="Maturity Date"
          startDate={maturityDateFilter.startDate}
          endDate={maturityDateFilter.endDate}
          relativeRange={maturityDateFilter.relativeRange}
          resetInput={0}
          error={false}
          setError={() => false}
          onChange={handleMaturityDateChange}
        />
        <div className="separator" />
        <button className="reset-button" onClick={resetFilters} type="button">
          <Icon name={ICONS.Clear} width={12} height={12} /> Reset
        </button>
      </div>
    );
  };

  const renderTotal = (total: number) => {
    const str = numeral(total).format('0,0');
    if (total === 10000) {
      return `more than ${str} records`;
    } else {
      return `${str} records`;
    }
  };

  const renderNoAccessMessage = () => {
    if ((filterError as any)?.response.status === 403) {
      return (
        <div className="no-access-ctx">
          <NoPermissions salesPersonEmail={profile?.assignedSalesPerson?.email || DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS} />
        </div>
      );
    }
  };

  return (
    <div className="cs-document-search-ctx">
      <div className="header-ctx">
        <div className="ctx">
          <div className="header">
            <div className="title">
              <span className="doc-type">{docType}</span> Precedent Search
            </div>
            <div className="separator" />
            <div className="nav-links">
              <NavLink to={DOCUMENT_SEARCH_ALL_ROUTE(docType) + `?${fetchUrlState()}`}>All</NavLink>
              <NavLink to={DOCUMENT_SEARCH_REPORTS_ROUTE(docType) + `?${fetchUrlState()}`}>Published Reports</NavLink>
              <NavLink to={DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE(docType) + `?${fetchUrlState()}`}>Source Documents</NavLink>
            </div>
          </div>
        </div>
      </div>
      <div className="body-ctx">
        {filters && !filtersLoading && (
          <>
            <div className="key-phrase ui left icon input">
              <input placeholder="Search by key phrase" value={text} onChange={(e) => setText(e.target.value)} onBlur={updateSearchPhrase} onKeyUp={(e) => e.key === 'Enter' && updateSearchPhrase()} />
              <i className="search icon"></i>
            </div>
            {renderFilters()}
          </>
        )}
        {(isLoading || filtersLoading) && <Loader className="cs-loader" position="center" />}
        {!filterError && !filtersLoading && !data && !isLoading && (
          <div className="no-content-ctx">
            <div className="no-content">
              <Icon name={ICONS.NoContent} height={200} width={200} />
              <div className="text">Search for a document or report using a key phrase or filters above</div>
            </div>
          </div>
        )}
        {renderNoAccessMessage()}
        {data && !isLoading && (
          <>
            <div className="info">
              <div className="main">Search results for {searchPhrase && <span>"{searchPhrase}" and</span>} applied filters</div>
              <div className="count">{renderTotal(data.pages[0]?.totalResults)}</div>
            </div>
            {searchPhrase && <DocumentSearchHighlights data={data} sort={sort} updateSort={updateSort} pageSize={PAGE_SIZE} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} />}
            {!searchPhrase && <DocumentSearchResultsTable data={data} updateSort={updateSort} reportType={reportType} pageSize={PAGE_SIZE} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} />}
          </>
        )}
      </div>
    </div>
  );
}

export default DocumentSearch;
